import { useEffect, useMemo, useState } from 'react';
import { LuSparkles, LuPlus } from 'react-icons/lu';
import { MdCancel } from 'react-icons/md';
import { Button } from '@/components/Button';
import { Textarea } from '@/components/Textarea';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import useFetchAiRecommendationReferenceItems from '../api/useFetchAiRecommendationReferenceItems';
import type { ReferenceSelection } from '../types/referenceSelection';
import formatInterviewedAt from '../lib/formatInterviewedAt';
import ReferenceItemsPickerDialog from './ReferenceItemsPickerDialog';

const PROMPT_PLACEHOLDER =
  '例）前職ではチームリードを経験しており、マネジメント思考が強い。ただし、現時点では肩書より裁量を重視している。';

/** プロンプト（自由文）の最大文字数 */
const MAX_PROMPT_LENGTH = 4000;

const EMPTY_SELECTION: ReferenceSelection = { customFieldIds: [], interviewLogIds: [] };

type Props = {
  candidateProfileId: number;
  /** 初期表示するプロンプト本文 */
  defaultPrompt?: string;
  /** 初期選択する参照情報（直近の検索で使った項目をプリセットする） */
  defaultSelection?: ReferenceSelection;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 初回構築か再構築かは親（AIRecommendationsPanel）が判断する */
  onSubmit: (values: { prompt: string; selection: ReferenceSelection }) => void;
  /** 処理中フラグ（親のフロー状態） */
  isSubmitting?: boolean;
};

/**
 * 検索条件をカスタマイズするモーダル
 *
 * - 参照情報: ReferenceItemsPickerDialog で参照する候補者情報・面談情報の項目を選択する
 * - プロンプト: 自由文を入力し、ペルソナ構築（または再構築）の入力として反映する
 */
export default function AICustomizeDialog({
  candidateProfileId,
  defaultPrompt = '',
  defaultSelection = EMPTY_SELECTION,
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
}: Props) {
  const { data: referenceItems, isLoading: isReferenceLoading } =
    useFetchAiRecommendationReferenceItems(open ? candidateProfileId : null);

  const [prompt, setPrompt] = useState(defaultPrompt);
  const [selection, setSelection] = useState<ReferenceSelection>(defaultSelection);
  const [isPicking, setIsPicking] = useState(false);

  // モーダルを開くたびに入力状態を初期化する（直近の検索条件をプリセット）。
  // defaultSelection は親で useMemo 安定化しているため deps に含めても無限ループしない。
  useEffect(() => {
    if (open) {
      setPrompt(defaultPrompt);
      setSelection(defaultSelection);
      setIsPicking(false);
    }
  }, [open, defaultPrompt, defaultSelection]);

  // 選択中のID→ラベルを解決するためのマップ（参照情報データから組み立てる）
  const customFieldLabelMap = useMemo(() => {
    const map = new Map<number, string>();
    referenceItems?.candidate_info_categories.forEach((category) => {
      category.custom_fields.forEach((field) => {
        map.set(field.id, field.name);
      });
    });
    return map;
  }, [referenceItems]);

  const interviewLogLabelMap = useMemo(() => {
    const map = new Map<number, string>();
    referenceItems?.interview_logs.forEach((log) => {
      map.set(log.id, `面談データ ${formatInterviewedAt(log.interviewed_at)}`);
    });
    return map;
  }, [referenceItems]);

  // チップ表示用に選択中の項目を {kind, id, label} 配列へ変換する
  const selectedChips = useMemo(() => {
    const chips: { kind: 'customField' | 'interviewLog'; id: number; label: string }[] = [];
    selection.customFieldIds.forEach((id) => {
      chips.push({ kind: 'customField', id, label: customFieldLabelMap.get(id) ?? '' });
    });
    selection.interviewLogIds.forEach((id) => {
      chips.push({ kind: 'interviewLog', id, label: interviewLogLabelMap.get(id) ?? '' });
    });
    return chips.filter((chip) => chip.label);
  }, [selection, customFieldLabelMap, interviewLogLabelMap]);

  const removeChip = (kind: 'customField' | 'interviewLog', id: number) => {
    setSelection((prev) =>
      kind === 'customField'
        ? { ...prev, customFieldIds: prev.customFieldIds.filter((x) => x !== id) }
        : { ...prev, interviewLogIds: prev.interviewLogIds.filter((x) => x !== id) }
    );
  };

  const submitting = Boolean(isSubmitting);
  const promptOverflow = prompt.length > MAX_PROMPT_LENGTH;
  const canSubmit = !submitting && !promptOverflow;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({ prompt, selection });
    onOpenChange(false);
  };

  return (
    <>
      {/* 参照情報選択モーダル表示中はこのダイアログを隠してネスト focus 問題を避ける */}
      <Dialog open={open && !isPicking} onOpenChange={(next) => !submitting && onOpenChange(next)}>
        <DialogContent className="flex w-[668px] max-w-[95vw] flex-col rounded-sm p-0 shadow-layer3">
          {/* ヘッダー */}
          <div className="flex shrink-0 items-center border-b border-solid border-gray-200 px-lg py-md">
            <DialogTitle className="text-base font-bold text-main">
              検索条件をカスタマイズ
            </DialogTitle>
          </div>

          {/* ボディ */}
          <div className="flex flex-col gap-lg overflow-y-auto p-lg">
            {/* 参照情報 */}
            <section className="flex flex-col gap-xs">
              <p className="text-heading-xxs text-main">参照情報</p>
              {selectedChips.length > 0 && (
                <div className="flex flex-wrap content-center items-center gap-xs rounded-sm border border-solid border-border bg-white p-sm">
                  {selectedChips.map((chip) => (
                    <span
                      key={`${chip.kind}_${chip.id}`}
                      className="inline-flex h-6 items-center gap-xxs rounded-full border border-solid border-border bg-white px-sm text-tag-sm text-main"
                    >
                      {chip.label}
                      <button
                        type="button"
                        onClick={() => removeChip(chip.kind, chip.id)}
                        aria-label={`${chip.label}を参照情報から除外する`}
                        className="flex items-center text-main opacity-50 hover:opacity-100"
                      >
                        <MdCancel size={20} aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <Button
                type="button"
                color="secondary"
                size="md"
                onClick={() => setIsPicking(true)}
                disabled={submitting}
                className="self-start"
              >
                <LuPlus size={20} aria-hidden="true" />
                追加
              </Button>
            </section>

            {/* プロンプト */}
            <section className="flex flex-col gap-xs">
              <p className="text-heading-xxs text-main">追加プロンプト</p>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={PROMPT_PLACEHOLDER}
                rows={5}
                className="resize-none overflow-y-auto"
                disabled={submitting}
                error={
                  promptOverflow ? `${MAX_PROMPT_LENGTH}文字以内で入力してください` : undefined
                }
                aria-label="プロンプト"
              />
            </section>
          </div>

          {/* フッター */}
          <div className="flex h-[75px] shrink-0 items-center justify-end gap-xs border-t border-solid border-gray-200 px-lg">
            <Button
              type="button"
              color="secondary"
              size="md"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              color="primary"
              size="md"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              <LuSparkles size={20} aria-hidden="true" />
              {submitting ? '検索中...' : 'この条件で検索'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ReferenceItemsPickerDialog
        open={isPicking}
        onOpenChange={setIsPicking}
        data={referenceItems}
        isLoading={isReferenceLoading}
        value={selection}
        onConfirm={setSelection}
      />
    </>
  );
}
